import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Grid,
  useMediaQuery,
  Drawer,
  IconButton,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MenuIcon from '@mui/icons-material/Menu';
import ConversationSidebar from '../components/ConversationSidebar';
import EntitySelector from '../components/EntitySelector';
import ChatInterface from '../components/ChatInterface';
import custodiaService from '../services/custodiaService';

const SIDEBAR_WIDTH = 280;

const CustodiaPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [creating, setCreating] = useState(false);
  const [creatingLoading, setCreatingLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const fetchConversations = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await custodiaService.getConversations('custodia');
      setConversations(res.data?.data || []);
    } catch { setConversations([]); }
    finally { setListLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await custodiaService.getStats();
      setStats(res.data?.data || null);
    } catch { setStats(null); }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchStats();
  }, [fetchConversations, fetchStats]);

  const handleSelectConversation = async (id) => {
    setSelectedId(id);
    setCreating(false);
    if (isMobile) setMobileOpen(false);
    try {
      const res = await custodiaService.getConversation(id);
      setSelectedMessages(res.data?.data?.messages || []);
    } catch { setSelectedMessages([]); }
  };

  const handleNewConversation = () => {
    setSelectedId(null);
    setSelectedMessages([]);
    setCreating(true);
    if (isMobile) setMobileOpen(false);
  };

  const handleStartConversation = async (sourceConfig) => {
    setCreatingLoading(true);
    try {
      const res = await custodiaService.createConversation(sourceConfig);
      const conv = res.data?.data;
      if (conv?.id) {
        setSelectedId(conv.id);
        setSelectedMessages([]);
        setCreating(false);
        fetchConversations();
        fetchStats();
      }
    } catch (err) {
      console.error('Error creating conversation:', err);
    } finally {
      setCreatingLoading(false);
    }
  };

  const handleDeleteConversation = async (id) => {
    try {
      await custodiaService.deleteConversation(id);
      if (selectedId === id) { setSelectedId(null); setSelectedMessages([]); }
      fetchConversations();
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  const sidebarContent = (
    <ConversationSidebar
      conversations={conversations}
      selectedId={selectedId}
      onSelect={handleSelectConversation}
      onNew={handleNewConversation}
      onDelete={handleDeleteConversation}
      loading={listLoading}
    />
  );

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(true)} size="small">
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ width: 36, height: 36, borderRadius: 0, bgcolor: 'rgba(89,128,166,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StorageIcon sx={{ fontSize: 20, color: '#5980a6' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1d1f20' }}>
              Custodia Digital
            </Typography>
            <Chip label="RAG" size="small" color="primary" sx={{ fontWeight: 600, height: 22 }} />
          </Box>
        </Box>
        {/* Stats chips */}
        <Grid container spacing={1} sx={{ width: 'auto', flexShrink: 0 }}>
          {[
            { icon: <FolderIcon sx={{ fontSize: 14 }} />, value: stats?.documentsReady ?? 0, label: 'Docs indexados', color: '#1565c0' },
            { icon: <ChatIcon sx={{ fontSize: 14 }} />, value: stats?.totalConversations ?? 0, label: 'Conversaciones', color: '#7b1fa2' },
            { icon: <SmartToyIcon sx={{ fontSize: 14 }} />, value: stats?.totalMessages ?? 0, label: 'Mensajes', color: '#e65100' },
          ].map((s, i) => (
            <Grid item key={i}>
              <Chip
                icon={s.icon}
                label={`${s.value} ${s.label}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: s.color, color: s.color,
                  '& .MuiChip-icon': { color: s.color } }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar - desktop */}
        {!isMobile && (
          <Paper elevation={0} sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, borderRight: 1, borderColor: 'divider', borderRadius: 0 }}>
            {sidebarContent}
          </Paper>
        )}

        {/* Sidebar - mobile drawer */}
        {isMobile && (
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
            {sidebarContent}
          </Drawer>
        )}

        {/* Chat area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {creating ? (
            <EntitySelector onStart={handleStartConversation} loading={creatingLoading} />
          ) : selectedId ? (
            <ChatInterface conversationId={selectedId} initialMessages={selectedMessages} />
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.6 }}>
              <StorageIcon sx={{ fontSize: 80, color: 'grey.300' }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                Custodia Digital con IA
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                Seleccione una conversación existente o cree una nueva para consultar documentos, correspondencias y expedientes con inteligencia artificial.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CustodiaPage;
